import React, { useEffect, useState } from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  zIndex?: number;
}

export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, subtitle, zIndex, children }) => {
  const [shouldRender, setShouldRender] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Small delay to allow exit animation (if we implement it)
      const timer = setTimeout(() => setShouldRender(false), 250);
      document.body.style.overflow = '';
      return () => clearTimeout(timer);
    }
    return () => {
        document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!shouldRender) return null;

  return (
    <div 
      className={`cab-modal-bg ${isOpen ? 'open' : ''}`} 
      onClick={onClose}
    >
      <div 
        className="cab-modal" 
        onClick={(e) => e.stopPropagation()} // Prevent clicks inside modal from closing it
      >
        <div className="cab-modal-header">
          <h3>{title}</h3>
          <button className="cab-modal-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        {subtitle && <p className="cab-modal-sub">{subtitle}</p>}
        
        <div className="cab-modal-content">
            {children}
        </div>
      </div>

      <style>{`
        .cab-modal-bg {
          position: fixed;
          inset: 0;
          background: rgba(8, 10, 41, 0.4);
          backdrop-filter: blur(3px);
          z-index: ${zIndex !== undefined ? zIndex : 60};
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .cab-modal-bg.open {
          opacity: 1;
        }
        .cab-modal {
          width: 100%;
          max-width: 480px; /* Match phone container width */
          background: #fff;
          border-radius: 24px;
          padding: 24px 20px;
          transform: scale(0.95);
          opacity: 0;
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .cab-modal-bg.open .cab-modal {
          transform: scale(1);
          opacity: 1;
        }
        .cab-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 6px;
        }
        .cab-modal-header h3 {
          font-size: 22px;
          font-weight: 600;
          color: var(--cab-violet);
          margin: 0;
        }
        .cab-modal-close {
          width: 40px;
          height: 40px;
          border-radius: 100px;
          background: var(--cab-light);
          border: none;
          color: var(--cab-violet);
          font-size: 18px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .cab-modal-sub {
          font-size: 14px;
          color: var(--cab-note);
          margin-bottom: 16px;
          margin-top: 0;
        }
      `}</style>
    </div>
  );
};
